import React, { createContext, useContext, useCallback } from 'react';
import { CreatorMode, CreatorCallbacks } from 'simple-creator/types';
import { useFormStore } from '../stores/formStore';
import { useDraftAutoSave } from '../hooks/useDraftAutoSave';

interface FormContextValue {
  mode: CreatorMode;
  config: any; // TODO: Type this properly
  callbacks: CreatorCallbacks;
  // Save-aware navigation
  handleNextStep: () => void;
  handlePreviousStep: () => void;
  triggerSave: (immediate?: boolean) => void;
}

const FormContext = createContext<FormContextValue | null>(null);

interface FormProviderProps {
  children: React.ReactNode;
  mode: CreatorMode;
  config: any;
  callbacks: CreatorCallbacks;
}

export const FormProvider: React.FC<FormProviderProps> = ({
  children,
  mode,
  config,
  callbacks,
}) => {
  const { nextStep, previousStep } = useFormStore();
  
  // Draft auto-save hook
  const { triggerSave } = useDraftAutoSave({
    enabled: config.autoSave,
    onSaveSuccess: (draftId) => callbacks.onDraftSave?.(draftId),
    onSaveError: (error) => console.error('[FormContext] Draft save error:', error),
  });

  // Save-aware navigation - triggers immediate save before step change
  const handleNextStep = useCallback(() => {
    triggerSave(true);
    nextStep();
  }, [triggerSave, nextStep]);

  const handlePreviousStep = useCallback(() => {
    triggerSave(true);
    previousStep();
  }, [triggerSave, previousStep]);
  
  const value: FormContextValue = {
    mode,
    config,
    callbacks,
    handleNextStep,
    handlePreviousStep,
    triggerSave,
  };
  
  return (
    <FormContext.Provider value={value}>
      {children}
    </FormContext.Provider>
  );
};

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within FormProvider');
  }
  return context;
};
